import React, { useCallback, useEffect, useMemo, useState } from 'react';

import SearchableSelect, { SearchableSelectOption } from '../SearchableSelect';
import {
  BankOptions,
  filterBankOptions,
  getBankNameFromOption,
  listEstateBanks
} from '../../resources/bankCodeList';
import { dangerNotification } from '../../services/notification';

type BankSelectProps = {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  fluid?: boolean;
  error?: boolean;
  openUpward?: boolean;
};

const mapBankToSelectOption = (bank: BankOptions): SearchableSelectOption => ({
  value: bank.value,
  label: bank.value,
  description: getBankNameFromOption(bank),
  searchText: bank.searchText
});

const BankSelect: React.FC<BankSelectProps> = ({
  value = '',
  onChange,
  onBlur,
  placeholder = 'Digite o código ou nome do banco',
  disabled = false,
  fluid = false,
  error = false,
  openUpward = true
}) => {
  const [banks, setBanks] = useState<BankOptions[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);

    listEstateBanks()
      .then(items => {
        if (active) setBanks(items);
      })
      .catch(() => {
        if (active) {
          dangerNotification('Erro', 'Erro ao carregar lista de bancos');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const options = useMemo(() => banks.map(mapBankToSelectOption), [banks]);

  const filterBankSelectOptions = useCallback(
    (_items: SearchableSelectOption[], query: string) =>
      filterBankOptions(banks, query).map(mapBankToSelectOption),
    [banks]
  );

  const renderOption = useCallback(
    (option: SearchableSelectOption) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 42,
            padding: '2px 8px',
            borderRadius: 4,
            background: '#59249b',
            color: '#fff',
            fontFamily: 'monospace',
            fontSize: 13,
            fontWeight: 700,
            flexShrink: 0
          }}
        >
          {option.label}
        </span>
        <span style={{ fontSize: 13, color: '#333', lineHeight: 1.35 }}>{option.description}</span>
      </div>
    ),
    []
  );

  return (
    <SearchableSelect
      options={options}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      loading={loading}
      disabled={disabled}
      fluid={fluid}
      error={error}
      openUpward={openUpward}
      noResultsMessage="Nenhum banco encontrado"
      filterOptions={filterBankSelectOptions}
      renderOption={renderOption}
      formatSelectedLabel={option =>
        option?.description ? `${option.label} · ${option.description}` : option?.label || ''
      }
    />
  );
};

export default BankSelect;
